import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';

function AdminTestTokens() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedToken, setCopiedToken] = useState(null);

  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || '/api');

  // Charger les tokens
  const loadTokens = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Vérifier que le token existe
      if (!token || token === 'null' || token === 'undefined') {
        setError('Non connecté. Redirection vers la page de connexion...');
        setTimeout(() => window.location.href = '/admin/login', 2000);
        return;
      }
      
      const response = await fetch(`${API_URL}/admin/test-tokens`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erreur lors du chargement');

      const data = await response.json();
      setTokens(data.tokens);
    } catch (err) {
      setError(err.message);
    }
  };

  // Générer un nouveau token
  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Vérifier que le token existe
      if (!token || token === 'null' || token === 'undefined') {
        setError('Non connecté. Redirection vers la page de connexion...');
        setLoading(false);
        setTimeout(() => window.location.href = '/admin/login', 2000);
        return;
      }
      
      const response = await fetch(`${API_URL}/admin/test-tokens/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ duration: 1 })
      });

      if (!response.ok) throw new Error('Erreur lors de la génération');

      const data = await response.json();
      
      // Copier automatiquement l'URL + date d'expiration
      const textToCopy = `${data.playlistUrl}\n⏰ Expire: ${new Date(data.expiresAt).toLocaleString('fr-FR')}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopiedToken(data.token);
      setTimeout(() => setCopiedToken(null), 3000);

      await loadTokens();
      
      alert(`✅ Token généré avec succès !\n\n📋 URL copiée dans le presse-papier\n\n⏰ Expire: ${new Date(data.expiresAt).toLocaleString('fr-FR')}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un token
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce token de test ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/test-tokens/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      await loadTokens();
    } catch (err) {
      setError(err.message);
    }
  };

  // Copier l'URL
  const handleCopy = async (url, token, expiresAt) => {
    try {
      const text = `${url}\n⏰ Expire: ${new Date(expiresAt).toLocaleString('fr-FR')}`;
      await navigator.clipboard.writeText(text);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      alert('Erreur lors de la copie');
    }
  };

  useEffect(() => {
    loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminLayout>
      <div className="admin-header">
        <h1>🎁 Tokens de Test</h1>
        <button 
          className="btn-back" 
          onClick={() => window.location.href = '/admin/home'}
        >
          ← Retour
        </button>
      </div>

      <div className="admin-actions">
        <button 
          className="btn-generate" 
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading ? '⏳ Génération...' : '➕ Générer un Token Test (1h)'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <div className="tokens-info">
        <p>💡 <strong>Info:</strong> Les tokens de test permettent aux utilisateurs de tester votre service pendant 1h avant de s'abonner.</p>
        <p>📊 Total: <strong>{tokens.length}</strong> token(s) de test</p>
      </div>

      <div className="tokens-grid">
        {tokens.map(token => (
          <div 
            key={token.id} 
            className={`token-card ${token.is_expired ? 'expired' : 'active'}`}
          >
            <div className="token-header">
              <span className={`status-badge ${token.is_expired ? 'expired' : 'active'}`}>
                {token.is_expired ? '❌ Expiré' : '✅ Actif'}
              </span>
              <span className="usage-badge">
                🔢 {token.usage_count} utilisation{token.usage_count !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="token-url">
              <input 
                type="text" 
                value={token.playlistUrl} 
                readOnly 
                onClick={(e) => e.target.select()}
              />
              <button 
                className={`btn-copy ${copiedToken === token.token ? 'copied' : ''}`}
                onClick={() => handleCopy(token.playlistUrl, token.token, token.expires_at)}
              >
                {copiedToken === token.token ? '✅ Copié' : '📋 Copier'}
              </button>
            </div>

            <div className="token-dates">
              <div className="date-info">
                <span className="label">Créé:</span>
                <span className="value">{new Date(token.created_at).toLocaleString('fr-FR')}</span>
              </div>
              <div className="date-info">
                <span className="label">Expire:</span>
                <span className="value">{new Date(token.expires_at).toLocaleString('fr-FR')}</span>
              </div>
            </div>

            <div className="token-actions">
              <button 
                className="btn-delete" 
                onClick={() => handleDelete(token.id)}
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        ))}

        {tokens.length === 0 && !loading && (
          <div className="empty-state">
            <p>Aucun token de test généré</p>
            <p>Cliquez sur "Générer un Token Test" pour créer votre premier lien de test</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminTestTokens;
